"use client";

import { useSearchParams } from "next/navigation";
import { createContext, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { type AdCode, type AdNetwork, adNetworkFor, fromHosts, type NetworkAds, type PageAds } from "@/lib/ads";
import { cn } from "@/lib/cn";
import { type ConsentChoice, readConsent, writeConsent } from "@/lib/consent";
import { track } from "@/lib/stats-client";
import type { VastTag } from "@/lib/site-defaults";
import { ConsentBanner } from "./Consent";
import { useSitePath } from "@/components/ui/use-site-path";
import { useUi } from "@/components/ui/use-ui";

// Partner ads on the page (codes and rules: lib/ads.ts). The provider works out which network the page
// is about, from the address or a page's <AdNetworkHint>, and the ads of only that network show: a card
// banner in the video feeds (AdCard) and, on computers, the network's widgets. Leaving that network's
// pages takes its widgets off the page again. Nothing shows before the page is interactive, so ads never
// hold up the first paint, nor before the site knows the visitor has no Premium (which takes all ads off
// until it ends).

/** Fired after a Premium purchase so the ads leave at once. */
export const MEMBERSHIP_CHANGED = "sweet:membership-changed";
/** Pages after which the visitor may have signed in or out: ask about Premium again. */
const AUTH_PAGES = ["/login", "/signup", "/account", "/subscription", "/reset-password"];
/**
 * Pages where passwords, reset links and payments are typed: no ad network's script runs on them (it would
 * run in our pages, able to read the form). Arriving at one from a page that had ads loads it afresh.
 */
const NO_AD_PAGES = ["/login", "/signup", "/account", "/subscription", "/reset-password", "/forgot-password", "/recover", "/invite"];
const noAdsOn = (pathname: string) => NO_AD_PAGES.some((p) => pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith(`${p}?`));

const AdsContext = createContext<{
  network: AdNetwork | null;
  ads: NetworkAds | null;
  /** OnlyFans creators' cards may show (Creators.tsx): set up, and the visitor has no Premium. */
  creators: boolean;
  /** The video ad before videos (Preroll.tsx), when set up and the visitor has no Premium. */
  preroll: Preroll | null;
  /** Consent to partners' cookies (lib/consent.ts): whether it's asked here, and reopening the choice. */
  consent: { required: boolean; reopen: () => void };
  /** Whether the visitor has Premium; null until known. */
  adFree: boolean | null;
  setHint: (n: AdNetwork | null) => void;
  setNoPopunder: (on: boolean) => void;
}>({
  network: null,
  ads: null,
  creators: false,
  preroll: null,
  consent: { required: false, reopen: () => {} },
  adFree: null,
  setHint: () => {},
  setNoPopunder: () => {},
});

export interface Preroll {
  /** Tried in order until one has an ad, those for this device (Preroll.tsx). */
  vastTags: VastTag[];
  everyMinutes: number;
}

export const useAds = () => useContext(AdsContext);

/**
 * `creators`: OnlyTraffic is set up (widget id and key), so the creators' cards can show. `preroll`:
 * the video ad before videos, when one is set (Admin → Settings).
 */
export function AdsProvider({ ads, creators, preroll, children }: { ads: PageAds; creators: boolean; preroll: Preroll | null; children: React.ReactNode }) {
  const [route, setRoute] = useState<{ pathname: string; platform: string | null } | null>(null);
  const [hint, setHint] = useState<AdNetwork | null>(null);
  const [noPopunder, setNoPopunder] = useState(false);
  // Unknown until asked: no ads meanwhile, so a member never sees one flash by.
  const [adFree, setAdFree] = useState<boolean | null>(null);
  // Where consent to partners' cookies is required, none of their ads load before the visitor accepts
  // (undefined: the choice stored in the browser not read yet).
  const [consentRequired, setConsentRequired] = useState<boolean | null>(null);
  const [consent, setConsent] = useState<ConsentChoice | null | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    const check = () =>
      fetch("/api/me/membership", { cache: "no-store" })
        .then((r) => (r.ok ? (r.json() as Promise<{ adFree?: boolean; consentRequired?: boolean }>) : null))
        .then((m) => {
          if (!alive) return;
          setAdFree(!!m?.adFree);
          setConsentRequired(m?.consentRequired !== false);
          setConsent(readConsent());
        })
        .catch(() => {
          if (!alive) return;
          setAdFree(false);
          setConsentRequired(true);
          setConsent(readConsent());
        });
    check();
    window.addEventListener(MEMBERSHIP_CHANGED, check);
    return () => {
      alive = false;
      window.removeEventListener(MEMBERSHIP_CHANGED, check);
    };
  }, []);

  // Coming back from signing in, out or buying: ask again.
  const lastPath = useRef<string | null>(null);
  useEffect(() => {
    if (!route) return;
    const previous = lastPath.current;
    lastPath.current = route.pathname;
    if (previous && previous !== route.pathname && AUTH_PAGES.some((p) => previous.startsWith(p))) window.dispatchEvent(new Event(MEMBERSHIP_CHANGED));
  }, [route]);

  // Ads only for visitors without Premium, and where consent is required, once they have accepted; never on
  // the pages where passwords and payments are typed.
  const sensitive = !!route && noAdsOn(route.pathname);
  const adsOn = adFree === false && consentRequired !== null && (!consentRequired || consent === "all") && !sensitive;
  const adsLoaded = useRef(false);
  useEffect(() => {
    if (adsOn && route) adsLoaded.current = true;
    // Scripts an ad network already ran stay in the page: a page for passwords starts afresh without them.
    if (sensitive && adsLoaded.current) window.location.reload();
  }, [adsOn, route, sensitive]);
  const network = route && adsOn ? (hint ?? adNetworkFor(route.pathname, route.platform, ads.defaultNetwork)) : null;
  const creatorsOn = creators && adsOn;
  const prerollOn = adsOn ? preroll : null;
  const choose = useCallback((choice: ConsentChoice | null) => {
    writeConsent(choice);
    setConsent(choice);
  }, []);
  const consentState = useMemo(() => ({ required: !!consentRequired && adFree === false, reopen: () => choose(null) }), [consentRequired, adFree, choose]);
  const value = useMemo(
    () => ({ network, ads: (network && ads.networks[network]) || null, creators: creatorsOn, preroll: prerollOn, consent: consentState, adFree, setHint, setNoPopunder }),
    [network, ads, creatorsOn, prerollOn, consentState, adFree],
  );
  const widgets = useMemo(() => (value.ads && noPopunder ? { ...value.ads, popunder: undefined } : value.ads), [value.ads, noPopunder]);
  return (
    <AdsContext.Provider value={value}>
      {children}
      {consentRequired && adFree === false && consent === null && <ConsentBanner onChoose={choose} />}
      <Suspense fallback={null}>
        <RouteWatcher onChange={setRoute} />
      </Suspense>
      {widgets && network && <AdWidgets key={network} network={network} ads={widgets} />}
    </AdsContext.Provider>
  );
}

/** "Cookie settings" in the footer where consent is asked: shows the choice again. */
export function CookieSettingsLink({ className }: { className?: string }) {
  const { consent } = useAds();
  const t = useUi();
  if (!consent.required) return null;
  return (
    <button type="button" onClick={consent.reopen} className={className}>
      {t.consent.settings}
    </button>
  );
}

function RouteWatcher({ onChange }: { onChange: (route: { pathname: string; platform: string | null }) => void }) {
  const pathname = useSitePath();
  const platform = useSearchParams().get("platform");
  useEffect(() => onChange({ pathname, platform }), [pathname, platform, onChange]);
  return null;
}

/** A page about one network that the address doesn't name (a LiveJasmin video) says so. */
export function AdNetworkHint({ network }: { network: AdNetwork }) {
  const { setHint } = useAds();
  useEffect(() => {
    setHint(network);
    return () => setHint(null);
  }, [network, setHint]);
  return null;
}

/**
 * A page that plays a tube site's video in that site's player (lib/frame-sources.ts) opens no popunder:
 * FlyFLV forbids them on pages with its videos, and Aylo any ads around Pornhub's. The network's other
 * ads stay.
 */
export function NoPopunder() {
  const { setNoPopunder } = useAds();
  useEffect(() => {
    setNoPopunder(true);
    return () => setNoPopunder(false);
  }, [setNoPopunder]);
  return null;
}

/**
 * The network's card banner, the size of a video card (a 16:9 picture and its caption) so the grid keeps
 * its rhythm, marked as an ad. Its frame may open the network in a new tab, nothing more.
 */
export function AdCard({ className }: { className?: string }) {
  const t = useUi();
  const { ads, network } = useAds();
  const card = ads?.card;
  if (!card) return null;
  return (
    <div className={cn("min-w-0", className)} data-ad={network}>
      <div className="relative w-full overflow-hidden rounded-lg bg-surface ring-1 ring-white/[0.04]" style={{ paddingBottom: "calc(56.25% + 50px)" }}>
        <iframe
          src={card.src}
          title={t.ads.advertisement}
          loading="lazy"
          scrolling="no"
          referrerPolicy="no-referrer-when-downgrade"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
          className="absolute inset-0 size-full border-0"
        />
      </div>
      <p className="mt-2 text-[11px] uppercase tracking-[0.12em] text-faint">{t.video.sponsored}</p>
    </div>
  );
}

/**
 * The network's top banner (728×90) in the empty top right of the live cams pages, on wide screens only:
 * its iframe, or for Stripchat a strip of its live models. Marked as an ad; nothing for Premium members.
 * The page places it (`className` with absolute or relative positioning, which its "Ad" mark needs).
 */
export function TopAd({ className }: { className?: string }) {
  const t = useUi();
  const { ads, network } = useAds();
  const top = ads?.top;
  if (!top || !network) return null;
  return (
    <div data-ad={network} className={cn("hidden h-[90px] w-[728px] overflow-hidden rounded-lg bg-surface ring-1 ring-white/[0.06] min-[1500px]:block", className)}>
      {top.kind === "iframe" ? (
        <iframe
          src={top.src}
          title={t.ads.advertisement}
          width={728}
          height={90}
          scrolling="no"
          referrerPolicy="no-referrer-when-downgrade"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
          className="block size-full border-0"
        />
      ) : network === "stripchat" ? (
        <StripchatStrip landing={top.src} />
      ) : null}
      <span className="pointer-events-none absolute right-1.5 top-0.5 text-[8.5px] uppercase tracking-[0.12em] text-white/50">{t.ads.ad}</span>
    </div>
  );
}

const STRIP_SIZE = 4;
const STRIP_REFRESH_MS = 60_000;

interface WidgetModel {
  username?: string;
  status?: string;
  viewersCount?: number;
  snapshotUrl?: string;
  widgetPreviewUrl?: string;
}

/**
 * Stripchat's top banner: its live models from Stripcash's widget API, asked by the visitor's browser
 * (so Stripchat applies the models' location restrictions for this visitor itself), each linking to her
 * room through our landing link (its userId). Refreshed every minute while on screen.
 */
function StripchatStrip({ landing }: { landing: string }) {
  const t = useUi();
  const [models, setModels] = useState<WidgetModel[]>([]);
  const userId = useMemo(() => {
    try {
      return new URL(landing).searchParams.get("userId");
    } catch {
      return null;
    }
  }, [landing]);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    const load = () =>
      fetch(`https://go.whitetrafsa.com/api/models?${new URLSearchParams({ limit: String(STRIP_SIZE), userId })}`, { credentials: "omit" })
        .then((r) => (r.ok ? (r.json() as Promise<{ models?: WidgetModel[] }>) : null))
        .then((d) => alive && d?.models && setModels(d.models.filter((m) => m.username && m.status === "public").slice(0, STRIP_SIZE)))
        .catch(() => {});
    load();
    const timer = window.setInterval(() => document.visibilityState === "visible" && load(), STRIP_REFRESH_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [userId]);

  if (!userId || !models.length) return null;
  const room = (username: string) => {
    const url = new URL(landing);
    url.searchParams.set("path", username);
    return url.toString();
  };
  return (
    <div className="flex h-full items-stretch gap-1.5 p-1.5">
      <a
        href={landing}
        target="_blank"
        rel="noopener sponsored"
        className="flex w-[92px] shrink-0 flex-col justify-center rounded-md bg-[#a33a3a] px-2 text-[11px] font-semibold uppercase leading-tight tracking-wide text-white"
      >
        <span className="text-[13px]">Stripchat</span>
        <span className="mt-0.5 font-normal normal-case text-white/80">{t.video.liveNowFree}</span>
      </a>
      {models.map((m) => (
        <a
          key={m.username}
          href={room(m.username!)}
          target="_blank"
          rel="noopener sponsored"
          data-stat="room:stripchat:strip"
          title={m.username}
          className="group/strip relative min-w-0 flex-1 overflow-hidden rounded-md bg-ink"
        >
          {/* eslint-disable-next-line @next/next/no-img-element -- live snapshots straight from Stripchat's CDN, never stored */}
          <img
            src={m.snapshotUrl || m.widgetPreviewUrl}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover transition-transform duration-300 group-hover/strip:scale-105"
          />
          <span className="absolute left-1 top-1 rounded bg-brand-strong px-1 text-[9.5px] font-semibold uppercase leading-[15px] text-white">{t.cams.live}</span>
          <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/85 to-transparent px-1.5 pb-1 pt-3 text-[11px] text-white">
            {m.username}
          </span>
        </a>
      ))}
    </div>
  );
}

/** Instant messages show at most this often (the other widgets carry their own limits). */
const IM_EVERY_MS = 12 * 3600_000;
const IM_SEEN = "sweet:ad-im";
/**
 * Instant messages that slide in and out until closed: shown once, then taken away after this long
 * (Chaturbate's box, by its id).
 */
const IM_ONCE: Partial<Record<AdNetwork, { id: string; ms: number }>> = { chaturbate: { id: "im_popupFixed", ms: 16_000 } };
/** Widgets wait this long, so a page that turns out to be about another network never starts them. */
const SETTLE_MS = 2500;
/** The chat head's box (its script looks it up by this id), and the class that opens its panel. */
const CHAT_HEAD_ID = "bcch_tool";
const CHAT_HEAD_OPEN = "__bcch_open";

/**
 * The network's scripts, on computers only. While they are on, anything they add to the page (boxes in
 * <body>, styles and scripts in <head>) and the click handlers they set are noted, and removed when the
 * visitor leaves this network's pages.
 */
function AdWidgets({ network, ads }: { network: AdNetwork; ads: NetworkAds }) {
  const [desktop, setDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const scripts = useMemo(
    () =>
      (["footerBar", "chatHead", "instantMessage", "popunder"] as const)
        .map((f) => [f, ads[f]] as const)
        .filter((e): e is [typeof e[0], AdCode] => e[1]?.kind === "script"),
    [ads],
  );

  useEffect(() => {
    if (!desktop || !scripts.length) return;
    const hosts = [...new Set(scripts.map(([, c]) => new URL(c.src).host))];
    const added = new Set<Node>();
    const ours = (n: Node) =>
      n instanceof Element &&
      (n.tagName.startsWith("NEXT") ||
        n.hasAttribute("data-precedence") ||
        /\/_next\//.test(n.getAttribute("src") ?? n.getAttribute("href") ?? "") ||
        n.tagName === "TITLE" ||
        n.tagName === "META");
    const observer = new MutationObserver((records) => records.forEach((r) => r.addedNodes.forEach((n) => !ours(n) && added.add(n))));
    observer.observe(document.body, { childList: true });
    observer.observe(document.head, { childList: true });

    // Handlers the ad scripts add to the window or document (e.g. the popunder's click), told apart by
    // where the call comes from.
    const listeners: [EventTarget, string, EventListenerOrEventListenerObject, boolean | AddEventListenerOptions | undefined][] = [];
    const originals = new Map<EventTarget, EventTarget["addEventListener"]>();
    for (const target of [window, document, document.body] as EventTarget[]) {
      const original = target.addEventListener;
      originals.set(target, original);
      target.addEventListener = function (this: EventTarget, type: string, fn: EventListenerOrEventListenerObject | null, opts?: boolean | AddEventListenerOptions) {
        if (fn && fromHosts(new Error().stack, hosts)) {
          listeners.push([target, type, fn, opts]);
          // Scripts written to start on the page's load (Chaturbate's) come after it: start them now.
          const late = (type === "load" && target === window && document.readyState === "complete") || (type === "DOMContentLoaded" && document.readyState !== "loading");
          if (late) window.setTimeout(() => (typeof fn === "function" ? fn.call(target, new Event(type)) : fn.handleEvent(new Event(type))), 0);
        }
        return original.call(this, type, fn, opts);
      } as EventTarget["addEventListener"];
    }
    // document.write does nothing once the page has loaded; an ad script's (its styles) goes in at the end.
    const write = document.write;
    document.write = function (this: Document, ...html: string[]) {
      if (fromHosts(new Error().stack, hosts)) document.body.insertAdjacentHTML("beforeend", html.join(""));
      else write.apply(this, html);
    };

    const timers: number[] = [];
    const watchers: (() => void)[] = [];

    // The chat head stays just the model's picture: its panel ("Special offer!") opens only when the
    // visitor clicks the picture, never by itself.
    if (scripts.some(([f]) => f === "chatHead")) {
      // It may not come at all (it shows once a day): stop looking after a minute.
      const since = Date.now();
      const poll = window.setInterval(() => {
        const box = document.getElementById(CHAT_HEAD_ID);
        if (!box && Date.now() - since < 60_000) return;
        window.clearInterval(poll);
        if (!box) return;
        let clicked = 0;
        const onPointer = () => (clicked = Date.now());
        box.addEventListener("pointerdown", onPointer, true);
        const keepClosed = () => {
          if (box.classList.contains(CHAT_HEAD_OPEN) && Date.now() - clicked > 1500) box.classList.remove(CHAT_HEAD_OPEN);
        };
        keepClosed();
        const watch = new MutationObserver(keepClosed);
        watch.observe(box, { attributes: true, attributeFilter: ["class"] });
        watchers.push(() => {
          watch.disconnect();
          box.removeEventListener("pointerdown", onPointer, true);
        });
      }, 300);
      watchers.push(() => window.clearInterval(poll));
    }
    const inject = (code: AdCode) => {
      if (code.id && document.getElementById(code.id)) return;
      const script = document.createElement("script");
      script.src = code.src;
      if (code.id) script.id = code.id;
      script.async = true;
      document.body.appendChild(script);
    };
    for (const [format, code] of scripts) {
      if (format === "instantMessage") {
        // Later, and at most every 12 hours; it comes up bottom-left, away from the chat head (globals.css).
        timers.push(
          window.setTimeout(() => {
            let seen = 0;
            try {
              seen = Number(localStorage.getItem(`${IM_SEEN}:${network}`)) || 0;
            } catch {}
            if (Date.now() - seen < IM_EVERY_MS) return;
            try {
              localStorage.setItem(`${IM_SEEN}:${network}`, String(Date.now()));
            } catch {}
            inject(code);
            const once = IM_ONCE[network];
            if (once) timers.push(window.setTimeout(() => document.getElementById(once.id)?.remove(), once.ms));
          }, 30_000),
        );
      } else timers.push(window.setTimeout(() => inject(code), SETTLE_MS));
    }

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      watchers.forEach((stop) => stop());
      observer.disconnect();
      for (const [target, original] of originals) target.addEventListener = original;
      document.write = write;
      for (const [target, type, fn, opts] of listeners) target.removeEventListener(type, fn, opts);
      added.forEach((n) => n.parentNode?.removeChild(n));
    };
  }, [desktop, scripts, network]);

  // Unlike the scripts, the site's own popunder runs on phones too: it adds nothing to the page.
  return ads.popunder?.kind === "link" ? <PopunderLink network={network} landing={ads.popunder.src} /> : null;
}

/** A landing-link popunder shows at most this often (as Stripcash's own script does). */
const PU_EVERY_MS = 12 * 3600_000;
const PU_SEEN = "sweet:ad-pu";

/**
 * A popunder run by the site (a network that gives a landing link, not a script to load), on computers
 * and phones alike: the first click on one of our own links opens that page in a new tab, where the
 * visitor carries on, and takes this tab to the network's landing page behind it. Links that already open in a new tab (the rooms,
 * which go to the network anyway) and clicks with a modifier key are left alone; at most every 12
 * hours, and never when the browser blocks the new tab.
 */
function PopunderLink({ network, landing }: { network: AdNetwork; landing: string }) {
  useEffect(() => {
    const key = `${PU_SEEN}:${network}`;
    const due = () => {
      try {
        return Date.now() - (Number(localStorage.getItem(key)) || 0) >= PU_EVERY_MS;
      } catch {
        return false;
      }
    };
    if (!due()) return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target instanceof Element ? e.target.closest("a[href]") : null;
      if (!(a instanceof HTMLAnchorElement) || a.closest("[data-ad]") || a.hasAttribute("download")) return;
      if ((a.target && a.target !== "_self") || a.origin !== window.location.origin || a.getAttribute("href")?.startsWith("#")) return;
      if (!due()) return document.removeEventListener("click", onClick, true);
      const opened = window.open(a.href, "_blank");
      if (!opened) return;
      e.preventDefault();
      e.stopPropagation();
      try {
        localStorage.setItem(key, String(Date.now()));
      } catch {}
      document.removeEventListener("click", onClick, true);
      track("popunder", network);
      window.location.href = landing;
    };
    // Capturing, so the page's own navigation (Next's links) doesn't run as well.
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [network, landing]);
  return null;
}
